import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FiSave, FiArrowLeft, FiHome, FiFileText, FiMapPin, FiRefreshCw } from 'react-icons/fi';
import { toast } from 'react-toastify';
import { congregacaoService } from '../../services/api';
import './CadastroCongregacao.css'; // Reutilizar o CSS do cadastro

const EditarCongregacao = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(true);
  const [formData, setFormData] = useState({
    nome: '',
    descricao: '',
    endereco: '',
    ativo: true
  });
  const [originalData, setOriginalData] = useState({});
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (id) {
      carregarCongregacao();
    }
  }, [id]);

  const carregarCongregacao = async () => {
    try {
      setLoadingData(true);
      const response = await congregacaoService.getById(id);
      
      if (response.success) {
        const congregacaoData = {
          nome: response.data.nome || '',
          descricao: response.data.descricao || '',
          endereco: response.data.endereco || '',
          ativo: response.data.ativo !== undefined ? response.data.ativo : true
        };
        setFormData(congregacaoData);
        setOriginalData(congregacaoData);
      } else {
        toast.error('Congregação não encontrada');
        navigate('/congregacoes');
      }
    } catch (error) {
      console.error('Erro ao carregar congregação:', error);
      toast.error('Erro ao carregar dados da congregação');
      navigate('/congregacoes');
    } finally {
      setLoadingData(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));

    // Limpar erro do campo quando o usuário começar a digitar
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.nome.trim()) {
      newErrors.nome = 'Nome da congregação é obrigatório';
    } else if (formData.nome.trim().length < 2) {
      newErrors.nome = 'Nome deve ter pelo menos 2 caracteres';
    } else if (formData.nome.trim().length > 100) {
      newErrors.nome = 'Nome deve ter no máximo 100 caracteres';
    }

    if (formData.descricao && formData.descricao.length > 500) {
      newErrors.descricao = 'Descrição deve ter no máximo 500 caracteres';
    }

    if (formData.endereco && formData.endereco.length > 255) {
      newErrors.endereco = 'Endereço deve ter no máximo 255 caracteres';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const hasChanges = () => {
    return JSON.stringify(formData) !== JSON.stringify(originalData);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      toast.error('Por favor, corrija os erros no formulário');
      return;
    }

    if (!hasChanges()) {
      toast.info('Nenhuma alteração foi feita');
      return;
    }

    setLoading(true);
    
    try {
      const dadosParaEnvio = {
        ...formData,
        nome: formData.nome.trim(),
        descricao: formData.descricao.trim(),
        endereco: formData.endereco.trim()
      };

      await congregacaoService.update(id, dadosParaEnvio);
      
      toast.success('Congregação atualizada com sucesso!');
      navigate('/congregacoes');
    } catch (error) {
      console.error('Erro ao atualizar congregação:', error);
      
      if (error.response?.status === 409) {
        toast.error('Já existe uma congregação com este nome');
      } else if (error.response?.status === 400) {
        toast.error(error.response.data.error || 'Dados inválidos');
      } else if (error.response?.status === 404) {
        toast.error('Congregação não encontrada');
        navigate('/congregacoes');
      } else {
        toast.error('Erro ao atualizar congregação. Tente novamente.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    if (hasChanges()) {
      if (window.confirm('Existem alterações não salvas. Deseja realmente sair?')) {
        navigate('/congregacoes');
      }
    } else {
      navigate('/congregacoes');
    }
  };

  const handleReset = () => {
    if (window.confirm('Deseja realmente desfazer todas as alterações?')) {
      setFormData(originalData);
      setErrors({});
      toast.info('Alterações desfeitas');
    }
  };

  if (loadingData) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Carregando dados da congregação...</p>
      </div>
    );
  }

  return (
    <div className="cadastro-congregacao-container">
      <div className="page-header">
        <div className="header-content">
          <div className="header-title">
            <FiHome className="page-icon" />
            <div>
              <h1>Editar Congregação</h1>
              <p className="page-description">Modificar informações da congregação</p>
            </div>
          </div>
          <div className="header-actions">
            {hasChanges() && (
              <button 
                type="button" 
                onClick={handleReset}
                className="btn-secondary"
                disabled={loading}
                title="Desfazer alterações"
              >
                <FiRefreshCw />
                Desfazer
              </button>
            )}
            <button 
              type="button" 
              onClick={handleCancel}
              className="btn-secondary"
              disabled={loading}
            >
              <FiArrowLeft />
              Voltar
            </button>
          </div>
        </div>
      </div>

      <div className="form-container">
        <form onSubmit={handleSubmit} className="congregacao-form">
          <div className="form-grid">
            {/* Nome da Congregação */}
            <div className="form-group">
              <label htmlFor="nome" className="form-label">
                <FiHome className="label-icon" />
                Nome da Congregação *
              </label>
              <input
                type="text"
                id="nome"
                name="nome"
                value={formData.nome}
                onChange={handleInputChange}
                className={`form-input ${errors.nome ? 'error' : ''}`}
                placeholder="Digite o nome da congregação"
                maxLength={100}
                disabled={loading}
                autoFocus
              />
              {errors.nome && <span className="error-message">{errors.nome}</span>}
            </div>

            {/* Endereço */}
            <div className="form-group">
              <label htmlFor="endereco" className="form-label">
                <FiMapPin className="label-icon" />
                Endereço
              </label>
              <input
                type="text"
                id="endereco"
                name="endereco"
                value={formData.endereco}
                onChange={handleInputChange}
                className={`form-input ${errors.endereco ? 'error' : ''}`}
                placeholder="Digite o endereço da congregação"
                maxLength={255}
                disabled={loading}
              />
              {errors.endereco && <span className="error-message">{errors.endereco}</span>}
            </div>

            {/* Descrição */}
            <div className="form-group full-width">
              <label htmlFor="descricao" className="form-label">
                <FiFileText className="label-icon" />
                Descrição
              </label>
              <textarea
                id="descricao"
                name="descricao"
                value={formData.descricao}
                onChange={handleInputChange}
                className={`form-textarea ${errors.descricao ? 'error' : ''}`}
                placeholder="Digite uma descrição para a congregação (opcional)"
                rows={4}
                maxLength={500}
                disabled={loading}
              />
              <div className="textarea-footer">
                {errors.descricao && <span className="error-message">{errors.descricao}</span>}
                <span className="char-count">
                  {formData.descricao.length}/500 caracteres
                </span>
              </div>
            </div>

            {/* Status Ativo */}
            <div className="form-group full-width">
              <div className="checkbox-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    name="ativo"
                    checked={formData.ativo}
                    onChange={handleInputChange}
                    className="checkbox-input"
                    disabled={loading}
                  />
                  <span className="checkbox-custom"></span>
                  <span className="checkbox-text">
                    Congregação ativa
                    <small>Congregações inativas não aparecerão nas listagens principais</small>
                  </span>
                </label>
              </div>
            </div>
          </div>

          {/* Botões de Ação */}
          <div className="form-actions">
            <button
              type="button"
              onClick={handleCancel}
              className="btn-secondary"
              disabled={loading}
            >
              <FiArrowLeft />
              Cancelar
            </button>
            
            <button
              type="submit"
              className="btn-primary"
              disabled={loading || !hasChanges()}
            >
              {loading ? (
                <>
                  <div className="loading-spinner"></div>
                  Salvando...
                </>
              ) : (
                <>
                  <FiSave />
                  Salvar Alterações
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditarCongregacao;